import { z } from 'zod/mini'
import { ClientError } from '../../../../common/utils/Exception'

/** ファイルの状態定数 */
export const FILE_STATE = Object.freeze({
  PENDING_VIRUS_CHECK: 'pending-virus-check',
  VIRUS_CHECK_IN_PROGRESS: 'virus-check-in-progress',
  VIRUS_CHECK_ERROR: 'virus-check-error',
  VALIDATION_ERROR: 'validation-error',
  UPLOADED: 'uploaded',
  PENDING_DELETION: 'pending-deletion'
})

/** ファイル形式定数 */
export const FILE_TYPE = Object.freeze({
  CSV: 'csv',
  EXCEL: 'excel'
})

/** Zod: ファイル種別1件（サービス戻り値） */
export const FileClassificationSchema = z.object({
  fileClassification: z.string(),
  isDefault: z.boolean(),
  isInUse: z.boolean(),
  isPendingDelete: z.optional(z.boolean())
})

/** Zod: ファイル種別配列（_fetchDefaultFileClassificationsFromAppMaster の戻り値） */
export const FileClassificationsArraySchema = z.array(FileClassificationSchema)

/** Zod: ファイル種別リスト（fetchFileClassificationList の body） */
export const FileClassificationListStructureSchema = z.object({
  defaultTypes: z.array(FileClassificationSchema),
  userTypes: z.array(FileClassificationSchema)
})

/** Zod: データ作成タスク（fetchDataCreateTask の body） */
export const DataCreateTaskBodySchema = z.object({
  periodKind: z.string(),
  periodValue: z.string(),
  taskKey: z.string()
})

/** Zod: ID変換テーブル1件（fetchIDConversionTables の body 要素） */
export const IDConversionTableSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  recordCount: z.number(),
  updatedAt: z.string(),
  mappings: z.array(z.object({ from: z.string(), to: z.string() }))
})

/** Zod: ファイル種別使用中チェック（checkFileClassificationInUse の body） */
export const CheckFileClassificationInUseBodySchema = z.object({
  isInUse: z.boolean()
})

/** Zod: 除外行条件 */
const ExcludedRowConditionSchema = z.object({
  column: z.string(),
  values: z.array(z.string())
})

/** Zod: ファイル状態1件（fetchExistingFiles の body 要素） */
export const FileStateSchema = z.object({
  fileKey: z.string(),
  fileName: z.string(),
  fileSize: z.number(),
  fileType: z.enum(Object.values(FILE_TYPE)),
  fileState: z.enum(Object.values(FILE_STATE)),
  headers: z.array(z.string()),
  rowCount: z.optional(z.number()), // IF02101 row_count。ローカル未保持時の保存で既存行数を維持するために使う
  fileClassification: z.string(),
  primaryKeyColumn: z.string(),
  dataCategory: z.string(), // データ種別（'employee' / 'company' / ''（未選択））。ファイル種別選択時は必須
  idConversionTable: z.optional(z.nullable(z.string())), // 空文字列または null/undefined で「変換なし」
  exclusionConditions: z.array(ExcludedRowConditionSchema),
  registeredExclusionConditions: z.optional(z.array(ExcludedRowConditionSchema)),
  hashColumns: z.optional(z.array(z.string())),
  registeredHashColumns: z.optional(z.array(z.string())),
  sheetName: z.string(),
  sheetList: z.array(z.string()),
  uploadedUsername: z.string(),
  uploadedDateTime: z.string(),
  isStoredLocally: z.boolean(),
  isUploadedLocally: z.boolean(),
  isChanged: z.boolean(),
  isBaseFile: z.optional(z.boolean()),
  isDownloading: z.optional(z.boolean()),
  updateUserName: z.string(),
  updatedAt: z.string()
})

/** Zod: ファイル状態配列（fetchExistingFiles の upload_files） */
export const FileStateArraySchema = z.array(FileStateSchema)

/** Zod: IF02101 レスポンスボディ */
export const GetGenericTaskFilesBodySchema = z.object({
  upload_files: FileStateArraySchema,
  used_mapping_file_types: z.array(z.string())
})

/** Zod: ID変換テーブル配列（fetchIDConversionTables の body） */
export const IDConversionTableArraySchema = z.array(IDConversionTableSchema)

/**
 * Zod: IF02104 過去タスクファイル設定（正規化後）。 IF Response の settings に対応する（join_column_list は1要素想定のため primaryKey /
 * conversionTableKey に平坦化して正規化する）
 */
export const PreviousFileSettingsSchema = z.object({
  dataCategory: z.string(), // data_category（null は '' に正規化）
  primaryKey: z.string(), // join_column_list[0].primary_key
  conversionTableKey: z.string(), // join_column_list[0].conversion_table_key
  excludedRowConditionList: z.array(ExcludedRowConditionSchema), // excluded_row_condition_list
  hashColumnList: z.array(z.string()), // hash_column_list
  isBaseFile: z.boolean() // is_base_file（月次タスクのみ画面側で使用）
})

/** Zod: IF02104 過去タスクファイル設定取得 レスポンスボディ（正規化後） */
export const GetPreviousFileSettingsBodySchema = z.object({
  found: z.boolean(),
  settings: z.nullable(PreviousFileSettingsSchema)
})

/** デフォルト: ファイル種別リスト（空） */
export const DEFAULT_FILE_CLASSIFICATIONS = Object.freeze([])

/** デフォルト: ファイル種別リスト取得の戻り値 */
export const DEFAULT_FILE_CLASSIFICATION_LIST = Object.freeze({
  body: { defaultTypes: [], userTypes: [] }
})

/** デフォルト: データ作成タスク取得の戻り値 */
export const DEFAULT_DATA_CREATE_TASK = Object.freeze({
  body: { periodKind: '', periodValue: '', taskKey: '' }
})

/** デフォルト: 既存ファイル一覧の戻り値 */
export const DEFAULT_EXISTING_FILES = Object.freeze({ body: [] })

/** デフォルト: ID変換テーブル一覧の戻り値 */
export const DEFAULT_ID_CONVERSION_TABLES = Object.freeze({ body: [] })

/** デフォルト: ファイル種別使用中チェックの戻り値 */
export const DEFAULT_CHECK_FILE_CLASSIFICATION_IN_USE = Object.freeze({
  body: { isInUse: false }
})

/**
 * レスポンスをスキーマで検証し、失敗時は ClientError を投げる
 *
 * @template T
 * @param {z.ZodType<T>} schema
 * @param {unknown} value
 * @param {string} [message]
 * @returns {T}
 * @throws {ClientError}
 */
export function parseResponseOrThrow(schema, value, message = 'レスポンス形式が不正です') {
  const result = schema.safeParse(value)
  if (result.success) return result.data
  throw new ClientError(message, { zodError: result.error })
}

/**
 * @typedef {Object} FileState
 * @property {string} fileKey // nanoidで生成された一意のキー
 * @property {string} fileName
 * @property {number} fileSize
 * @property {'csv' | 'excel'} fileType
 * @property {(typeof FILE_STATE)[keyof typeof FILE_STATE]} fileState
 * @property {string[]} headers
 * @property {string} fileClassification // ファイル種別のラベル（例：「勤怠情報」）※ラベル文字列そのものを識別子として使用
 * @property {string} primaryKeyColumn // ファイル種別選択時は必須
 * @property {string} dataCategory // データ種別（'employee'（従業員別） / 'company'（会社別） / ''（未選択））。ファイル種別選択時は必須
 * @property {string | null} idConversionTable // 空文字列または null で「変換なし」
 * @property {ExcludedRowCondition[]} exclusionConditions // 制限なし
 * @property {ExcludedRowCondition[]} registeredExclusionConditions // 登録確定時点の除外条件（値の解除不可）
 * @property {string[]} hashColumns // ハッシュ化対象カラム名の配列
 * @property {string[]} registeredHashColumns // 登録確定時点で確定したハッシュ化列（解除不可）
 * @property {string} sheetName // Excelの場合、最初のシートを自動選択
 * @property {string[]} sheetList // Excelの場合のみ
 * @property {string} uploadedUsername
 * @property {Date | string} uploadedDateTime
 * @property {boolean} isStoredLocally
 * @property {boolean} isUploadedLocally
 * @property {boolean} isBaseFile // ベースファイルフラグ
 * @property {boolean} isChanged // 変更フラグ（登録時の変更検知用）
 * @property {string} updateUserName // 最終更新者（保存時に設定）
 * @property {Date | string} updatedAt // 最終更新日時（保存時に設定）
 * @property {number} [contentRevision] // ファイル差し替えごとに加算される内容版数（ローカル専用。除外値候補などの再取得トリガー）
 */

/**
 * @typedef {Object} ExcludedRowCondition
 * @property {string} column // 除外対象のカラム名
 * @property {string[]} values // 除外する値の配列
 */

/**
 * @typedef {Object} FileClassification
 * @property {string} fileClassification // 種別のラベル（例：「勤怠情報」）※ラベル文字列そのものを識別子として使用
 * @property {boolean} isDefault // デフォルト種別かどうか（UI表示用フラグ、データソースから派生）
 * @property {boolean} isInUse // この種別が全タスクのいずれかのファイルで使用されているかどうか（削除判定用）
 * @property {boolean} [isPendingDelete] // 削除待ちかどうか（UI表示用フラグ）
 */

/**
 * @typedef {Object} FileClassificationListStructure
 * @property {FileClassification[]} defaultTypes // デフォルト種別（AppMasterから取得）
 * @property {FileClassification[]} userTypes // ユーザー追加種別（FileClassificationテーブルから取得）
 */

/**
 * @typedef {Object} IDConversionTable
 * @property {string} id // 変換表のID
 * @property {string} name // 変換表の名称
 * @property {string} description // 変換表の説明
 * @property {number} recordCount // 変換レコード数
 * @property {string} updatedAt // 更新日（YYYY-MM-DD形式）
 * @property {{ from: string; to: string }[]} mappings // マッピングデータ（配列形式）
 */

/**
 * @typedef {Object} FileManagementState
 * @property {'idle' | 'loading' | 'success' | 'error'} pageState
 * @property {string | null} taskId // ルート直下のUUID
 * @property {Object | null} dataCreateTask // データ作成タスク情報
 * @property {FileClassificationListStructure} fileClassificationList // ネストされた構造（defaultTypes + userTypes）
 * @property {Record<string, FileState>} fileList // Record<fileKey, FileState>
 * @property {Set<string>} expandedFiles // 詳細設定が展開中のファイルのfileKeyセット
 * @property {Record<string, FileState> | null} savedUploadState // 保存時のアップロード状態（変更検知用）
 * @property {string[]} usedMappingFileTypes // IF02101 used_mapping_file_types（⑦判定用）
 * @property {string[]} errorMessages
 */

/**
 * @typedef {Object} PreviousFileSettings IF02104 過去タスクファイル設定（正規化後）
 * @property {string} dataCategory // データ種別（'employee' / 'company' / ''）
 * @property {string} primaryKey // 過去タスクのキー項目カラム名
 * @property {string} conversionTableKey // 従業員ID変換表のキー
 * @property {ExcludedRowCondition[]} excludedRowConditionList // 除外条件
 * @property {string[]} hashColumnList // ハッシュ化対象列
 * @property {boolean} isBaseFile // ベースファイルフラグ（月次タスクのみ使用）
 */

/**
 * @typedef {Object} ParsedFileData
 * @property {string[]} headers
 * @property {string[][]} records
 * @property {number} recordCount
 * @property {string} sheetName
 * @property {string[]} sheetList
 */
